import { gql } from '@apollo/client'
import { Button, FormControl, Modal, Select } from '@farol-ds/react'
import classNames from 'classnames'
import type { Get } from 'type-fest'

import { InputIMask } from '../../components'
import { useValidateOabModalContext } from '../../context'
import styles from '../steps.module.scss'
import { ConfirmOab_RootFragment } from './__generated__/confirm-oab.graphql'

const oabTypes = [
  { acronym: 'A', description: 'A - Suplementar' },
  { acronym: 'B', description: 'B - Transferência' },
  { acronym: 'E', description: 'E - Estagiário' },
  { acronym: 'N', description: 'N - Normal' },
] as const

export type ConfirmOabProps = {
  root: ConfirmOab_RootFragment
}

type TagTopicEdge = NonNullable<Get<ConfirmOab_RootFragment, ['tag', 'states', 'edges']>>[number]
type Topic = NonNullable<Get<TagTopicEdge, ['node']>>
type TopicWithUf = Topic & { directoryUrl: NonNullable<Topic['directoryUrl']> }

export function ConfirmOab(props: ConfirmOabProps) {
  const { form, goNextSuccessStep } = useValidateOabModalContext()
  const {
    register,
    watch,
    setValue,
    formState: { errors, isValid },
    trigger,
  } = form

  const {
    root: { tag },
  } = props
  if (!tag?.states?.edges?.length) {
    console.warn('Unexpected warning: No states found in tag')
    return null
  }

  const states: TopicWithUf[] = tag.states.edges
    .filter((state): state is TagTopicEdge & { node: TopicWithUf } =>
      Boolean(state?.node?.directoryUrl)
    )
    .map((state) => ({ ...state.node, directoryUrl: state.node.directoryUrl.toUpperCase() }))

  return (
    <>
      <Modal.IconClose />
      <Modal.Header>
        <Modal.HeaderTitle>Confirme OAB</Modal.HeaderTitle>
        <Modal.HeaderDescription>
          Somente advogados(as) verificados podem acessar peças processuais.
        </Modal.HeaderDescription>
      </Modal.Header>
      <Modal.Body>
        <FormControl
          className={classNames(styles.formControlMargin, {
            [styles['formControlMargin--error']]: !!errors.oab,
          })}
          error={!!errors.oab}
        >
          <FormControl.Label>OAB</FormControl.Label>
          <InputIMask
            {...register('oab', { required: true, pattern: /^\d{1,6}$/ })}
            placeholder="______"
            type="text"
            mask={Number}
            maxLength={6}
            max={999999}
            min={0}
            scale={0}
            inputMode="numeric"
          />
          <FormControl.ErrorMessage>Digite uma OAB válida</FormControl.ErrorMessage>
        </FormControl>
        <FormControl
          className={classNames(styles.formControlMargin, {
            [styles['formControlMargin--error']]: !!errors.oabUf,
          })}
          error={!!errors.oabUf}
        >
          <FormControl.Label>Estado</FormControl.Label>
          <Select
            {...register('oabUf', { required: true, pattern: /^\d+$/ })}
            placeholder="Selecione o estado"
            value={watch(['oabUf', 'oabUfLabel']).filter(Boolean).join('|')}
            defaultValue=""
            onValueChange={(value) => {
              const [stateTid, stateUf] = value.split('|')
              setValue('oabUf', stateTid)
              setValue('oabUfLabel', stateUf)
              trigger('oabUf')
            }}
          >
            {states.map((state) => (
              <Select.Item key={state.tid} value={`${state.tid}|${state.directoryUrl}`}>
                {state.directoryUrl}
              </Select.Item>
            ))}
          </Select>
          <FormControl.ErrorMessage>Selecione um estado</FormControl.ErrorMessage>
        </FormControl>
        <FormControl
          className={classNames(styles.formControlMargin, {
            [styles['formControlMargin--error']]: !!errors.oabType,
          })}
          error={!!errors.oabType}
        >
          <FormControl.Label>Tipo</FormControl.Label>
          <Select
            {...register('oabType', {
              required: true,
              validate: (value) => oabTypes.some(({ acronym }) => acronym === value),
            })}
            placeholder="Selecione o tipo"
            value={watch('oabType')}
            defaultValue=""
            onValueChange={(value) => {
              setValue('oabType', value)
              trigger('oabType')
            }}
          >
            {oabTypes.map(({ acronym, description }) => (
              <Select.Item key={acronym} value={acronym}>
                {description}
              </Select.Item>
            ))}
          </Select>
          <FormControl.ErrorMessage>Selecione um tipo de OAB</FormControl.ErrorMessage>
        </FormControl>
      </Modal.Body>
      <Modal.Footer>
        <Button kind="primary" onClick={goNextSuccessStep} disabled={!isValid}>
          Avançar
        </Button>
      </Modal.Footer>
    </>
  )
}

ConfirmOab.fragments = {
  root: gql`
    fragment ConfirmOab_root on RootApi {
      tag(identifier: "ESTADOS") {
        states: topics(first: 27) {
          edges {
            node {
              tid
              directoryUrl
            }
          }
        }
      }
    }
  `,
}
